import React, { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { 
  User,
  CheckCircle,
  XCircle,
  AlertCircle,
  Eye,
  Wifi,
  WifiOff,
  RefreshCw
} from 'lucide-react';
import { Button } from '../../../components/shared';
import DataTable from '../../../components/shared/DataTable';
import ClientStats from '../../../components/admin/ClientStats';
import { 
  useClients, 
  useClientStats
} from '../../../services/clientService';
import { clientKeys } from '../../../services/clientService';

const AdminClients = () => {
  const [currentPage, setCurrentPage] = useState(1);
  
  const queryClient = useQueryClient();

  // API query parameters
  const queryParams = {
    sort_by: 'created_at',
    sort_order: 'desc',
    page: currentPage
  };

  // Fetch clients from API
  const { 
    data: clientsData, 
    isLoading: loading, 
    error: clientsError,
    refetch 
  } = useClients(queryParams);

  // Fetch client statistics
  const { 
    data: statsData, 
    isLoading: statsLoading
  } = useClientStats();

  // Extract clients and pagination from API response
  const clients = clientsData?.data || [];
  const pagination = clientsData?.pagination || null;

  // Calculate statistics based on total counts from all pages
  const totalClients = statsData?.data?.total_clients || 0;
  const activeClients = statsData?.data?.active_clients || 0;
  const inactiveClients = statsData?.data?.inactive_clients || 0;
  const verifiedClients = statsData?.data?.verified_clients || 0;

  // Helper function to check if client is online
  const isClientOnline = (client) => {
    // Use the server-calculated online status if available
    if (client.is_online !== undefined) {
      return client.is_online;
    }
    
    // Fallback to last login check for backward compatibility
    if (!client.last_logged_in) return false;
    
    const lastLoginTime = new Date(client.last_logged_in);
    const now = new Date();
    const timeDiff = now - lastLoginTime;
    
    // Consider client online if they logged in within the last 5 minutes
    const fiveMinutes = 5 * 60 * 1000; // 5 minutes in milliseconds
    return timeDiff < fiveMinutes;
  };

  // Helper function to get time since last activity
  const getTimeSinceLastActivity = (client) => {
    // Use last_activity if available, otherwise fallback to last_logged_in
    const activityTime = client.last_activity || client.last_logged_in;
    
    if (!activityTime) return "Never";
    
    const activityDate = new Date(activityTime);
    const now = new Date();
    const timeDiff = now - activityDate;
    
    const minutes = Math.floor(timeDiff / (1000 * 60));
    const hours = Math.floor(timeDiff / (1000 * 60 * 60));
    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  // Listen for logout events to refresh client data
  useEffect(() => {
    const handleLogout = () => {
      // Invalidate and refetch client data when a user logs out
      console.log('User logged out, refreshing client data...');
      queryClient.invalidateQueries({ queryKey: clientKeys.all });
    };

    // Listen for logout events from AuthContext
    window.addEventListener('userLoggedOut', handleLogout);

    return () => {
      window.removeEventListener('userLoggedOut', handleLogout);
    };
  }, [queryClient]);


  // Define columns for DataTable
  const columns = [
    {
      key: 'name',
      label: 'Client',
      sortable: true,
      searchable: true,
      mobilePrimary: true,
      exportValue: (client) =>
        [client.name || 'No Name', client.email].filter(Boolean).join(' · '),
      render: (client) => (
        <div className="flex items-center min-w-0">
          <div className="flex-shrink-0 h-10 w-10">
            {client.avatar ? (
              <img
                className="h-10 w-10 rounded-full object-cover"
                src={client.avatar}
                alt={client.name}
              />
            ) : (
              <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                <User className="h-5 w-5 text-gray-600" />
              </div>
            )}
          </div>
          <div className="ml-3 sm:ml-4 min-w-0">
            <div className="text-sm font-medium text-gray-900 truncate">
              {client.name || 'No Name'}
            </div>
            <div className="text-sm text-gray-500 truncate">{client.email}</div>
          </div>
        </div>
      )
    },
    {
      key: 'phone',
      label: 'Phone',
      sortable: true,
      searchable: true,
      exportValue: (client) => client.phone || 'N/A',
      render: (client) => client.phone || 'N/A'
    },
    {
      key: 'user_type',
      label: 'Type',
      sortable: true,
      exportValue: (client) => client.user_type || 'client',
      render: (client) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {client.user_type || 'client'}
        </span>
      )
    },
    {
      key: 'is_active',
      label: 'Account Status',
      sortable: true,
      exportValue: (client) => (client.is_active ? 'Active' : 'Inactive'),
      render: (client) => (
        <div className="flex items-center justify-end lg:justify-start">
          {client.is_active ? (
            <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
          ) : (
            <XCircle className="h-4 w-4 text-red-500 mr-2" />
          )}
          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            client.is_active 
              ? 'bg-green-100 text-green-800' 
              : 'bg-red-100 text-red-800'
          }`}>
            {client.is_active ? 'Active' : 'Inactive'}
          </span>
        </div>
      )
    },
    {
      key: 'online_status',
      label: 'Online Status',
      sortable: true,
      exportValue: (client) =>
        `${isClientOnline(client) ? 'Online' : 'Offline'} (${getTimeSinceLastActivity(client)})`,
      render: (client) => {
        const isOnline = isClientOnline(client);
        const timeAgo = getTimeSinceLastActivity(client);
        
        return (
          <div 
            className="flex items-center text-sm min-w-0 cursor-help justify-end lg:justify-start" 
            title={`Status: ${isOnline ? 'Online' : 'Offline'} | Last activity: ${client.last_activity ? new Date(client.last_activity).toLocaleString() : client.last_logged_in ? new Date(client.last_logged_in).toLocaleString() : 'Never'}${client.country ? ` | Location: ${client.country}` : ''}${client.ip_address ? ` | IP: ${client.ip_address}` : ''}`}
          >
            <div className="relative flex items-center">
              <div className="relative">
                {isOnline ? (
                  <div className="relative">
                    <div className="absolute -top-1 -left-1 w-3 h-3 bg-green-400 rounded-full animate-ping opacity-75"></div>
                    <div className="absolute -top-1 -left-1 w-3 h-3 bg-green-300 rounded-full animate-ping opacity-50" style={{ animationDelay: '0.5s' }}></div>
                    <div className="relative w-2 h-2 bg-green-500 rounded-full shadow-sm"></div>
                  </div>
                ) : (
                  <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                )}
              </div>
              
              <div className="ml-2 flex items-center">
                {isOnline ? (
                  <Wifi className="w-3 h-3 text-green-500 mr-1" />
                ) : (
                  <WifiOff className="w-3 h-3 text-gray-400 mr-1" />
                )}
                <div className="flex flex-col items-end lg:items-start">
                  <span className={`text-xs font-medium ${
                    isOnline ? 'text-green-700' : 'text-gray-600'
                  }`}>
                    {isOnline ? 'Online' : 'Offline'}
                  </span>
                  <span className="text-xs text-gray-500">
                    {timeAgo}
                  </span>
                  {client.country && (
                    <span className="text-xs text-gray-400 truncate max-w-16">
                      {client.country_code || client.country}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      },
    },
    {
      key: 'created_at',
      label: 'Joined',
      sortable: true,
      exportValue: (client) => {
        if (!client.created_at) return '';
        return new Date(client.created_at).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      },
      render: (client) => {
        const date = new Date(client.created_at);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      }
    }
  ];

  // Define actions for DataTable
  const actions = [
    {
      label: 'View',
      icon: Eye,
      onClick: (client) => {
        // Navigate to client detail page
        window.location.href = `/admin/clients/${client.id}`;
      },
      title: 'View Client Details',
      variant: 'default'
    }
  ];

  // Handle API errors
  if (clientsError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-500" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">Error Loading Clients</h3>
          <p className="mt-1 text-sm text-gray-500">
            {clientsError.message || 'Failed to load clients'}
          </p>
          <div className="mt-6">
            <Button onClick={() => refetch()}>
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (loading || statsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading clients...</p>
        </div>
      </div>
    );
  }

  const handleSearch = (data, searchTerm) => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return data;

    return data.filter((client) => {
      const haystack = [
        client.name,
        client.email,
        client.phone,
        client.user_type,
        client.country,
        client.country_code,
        client.is_active ? 'active' : 'inactive',
        isClientOnline(client) ? 'online' : 'offline',
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(term);
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-4 sm:p-6">

        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-3">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Clients</h1>
              <p className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600">Manage client accounts and information</p>
            </div>
            <button 
              onClick={() => {
                queryClient.invalidateQueries({ queryKey: clientKeys.all });
              }}
              disabled={loading}
              className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed self-start"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Client Statistics */}
        <div className="mb-6 sm:mb-8">
          <ClientStats
            totalClients={totalClients}
            activeClients={activeClients}
            inactiveClients={inactiveClients}
            verifiedClients={verifiedClients}
          />
        </div>

        {/* Clients DataTable */}
        <DataTable
          data={clients}
          columns={columns}
          actions={actions}
          loading={loading}
          searchable={true}
          filterable={false}
          sortable={true}
          selectable={false}
          exportable={true}
          exportFileName="clients"
          onSearch={handleSearch}
          pageSize={pagination?.per_page || 15}
          showPagination={false}
          searchPlaceholder="Search by name, email, phone, status…"
          emptyMessage="No clients found. Get started by adding a new client."
          className="mb-6"
        />

        {/* Server-side Pagination */}
        {pagination && pagination.last_page > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 bg-white rounded-b-lg">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="text-sm text-gray-700 text-center sm:text-left">
                Showing {pagination.from || 1} to {pagination.to || clients.length} of {pagination.total} results
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    if (pagination.current_page > 1) {
                      setCurrentPage(pagination.current_page - 1);
                    }
                  }}
                  disabled={pagination.current_page === 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <span className="hidden sm:inline">Previous</span>
                  <span className="sm:hidden">Prev</span>
                </button>
                
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, pagination.last_page) }, (_, i) => {
                    const page = i + 1;
                    return (
                      <button
                        key={page}
                        onClick={() => {
                          setCurrentPage(page);
                        }}
                        className={`px-3 py-1 text-sm border rounded-md ${
                          pagination.current_page === page
                            ? 'bg-primary-600 text-white border-primary-600'
                            : 'border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => {
                    if (pagination.current_page < pagination.last_page) {
                      setCurrentPage(pagination.current_page + 1);
                    }
                  }}
                  disabled={pagination.current_page === pagination.last_page}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  <span className="hidden sm:inline">Next</span>
                  <span className="sm:hidden">Next</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminClients;
